import { AiRateLimitDeniedReasonEnum } from '@ztechapps/common.contract'
import { jsonTryParse } from '@ztechapps/common.utils'

export type RateLimitDenial = {
  reason: AiRateLimitDeniedReasonEnum
  retryAfterSeconds: number
}

/**
 * Parse a `rateLimit.denied` payload from an AI-SDK error. The backend's
 * `AiRateLimiterMiddleware` responds with HTTP 429 and a JSON body shaped
 * `{ type: 'rateLimit.denied', reason, retryAfterSeconds }`. The Vercel AI
 * SDK surfaces the 429 body as `error.message`, so we try to parse it as
 * JSON and validate the shape. Returns `undefined` when the error is not
 * a rate-limit denial (e.g. 500, network failure, ordinary tool error).
 */
export const parseRateLimitDenial = (error: Error | undefined): RateLimitDenial | undefined => {
  if (!error?.message) return undefined
  const parsed = jsonTryParse(error.message)
  if (parsed?.type !== 'rateLimit.denied') return undefined
  const { reason, retryAfterSeconds } = parsed
  if (typeof retryAfterSeconds !== 'number' || retryAfterSeconds <= 0) return undefined
  if (reason !== AiRateLimitDeniedReasonEnum.SessionLimit && reason !== AiRateLimitDeniedReasonEnum.IpCeiling) {
    return undefined
  }
  return { reason, retryAfterSeconds }
}

export const formatRetryAfter = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`
  }
  const minutes = Math.ceil(seconds / 60)
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}
